import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getDeviceBrandListForSelect(
    $filter: deviceBrandFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getDeviceBrandList(
      filter: $filter
      pagination: $pagination
      sort: $sort
    ) {
      items {
        deviceType
        name
        id
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.DeviceBrandListState = {
  result: null,
  error: null,
  loading: false
};

export const getDeviceBrandListForSelectRequest = createAsyncThunk<
  TYPES.DeviceBrandList,
  {
    filter?: TYPES.DeviceBrandFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'device_brand/get_device_brand_list_for_select',
  async (
  params: {
    filter?: TYPES.DeviceBrandFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getDeviceBrandList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getDeviceBrandListForSelectSlice = createSlice({
  name: 'get_device_brand_list_for_select',
  initialState: initialState,
  reducers: {
    getDeviceBrandListForSelectClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getDeviceBrandListForSelectRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(
      getDeviceBrandListForSelectRequest.fulfilled,
      (state, action) => {
        state.result = action.payload;
        state.loading = false;
      }
    ).
    addCase(
      getDeviceBrandListForSelectRequest.rejected,
      (state, action) => {
        state.error = action.payload as Error || {
          message: 'Failed to fetch',
          statusCode: '500'
        };
        state.loading = false;
      }
    );
  }
});

export const { getDeviceBrandListForSelectClear } =
getDeviceBrandListForSelectSlice.actions;

export const getDeviceBrandListForSelect = createSelector(
  (state: RootState) =>
  state.deviceBrand.getDeviceBrandListForSelect.result,
  (state: RootState) =>
  state.deviceBrand.getDeviceBrandListForSelect.error,
  (state: RootState) =>
  state.deviceBrand.getDeviceBrandListForSelect.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getDeviceBrandListForSelectSlice.reducer;